// src/models/User.js - Core User Model with Validation
import { ValidationService } from '../services/ValidationService';

export class User {
  constructor(data = {}) {
    this.uid = data.uid || null;
    this.email = data.email || '';
    this.emailVerified = data.emailVerified || false;
    this.displayName = data.displayName || '';
    this.phoneNumber = data.phoneNumber || '';
    this.createdAt = data.createdAt || null;
    this.lastLoginAt = data.lastLoginAt || null;
    this.disabled = data.disabled || false;
    
    // Business-specific fields
    this.businessId = data.businessId || null; // Links to business profile
    this.role = data.role || 'owner'; // owner, employee, admin
    this.permissions = data.permissions || [];
  }

  // Validation Methods
  validate() {
    const errors = [];

    if (!ValidationService.isValidEmail(this.email)) {
      errors.push('Valid email address is required');
    }

    if (!ValidationService.isValidPhoneNumber(this.phoneNumber)) {
      errors.push('Valid phone number is required');
    }

    if (!this.displayName || this.displayName.trim().length < 2) {
      errors.push('Display name must be at least 2 characters');
    }

    if (!['owner', 'employee', 'admin'].includes(this.role)) {
      errors.push('Invalid user role');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Business Logic Methods
  isOwner() {
    return this.role === 'owner';
  }

  isEmployee() {
    return this.role === 'employee';
  }

  isAdmin() {
    return this.role === 'admin';
  }

  hasPermission(permission) {
    return this.permissions.includes(permission) || this.isOwner() || this.isAdmin();
  }

  canManageEmployees() {
    return this.hasPermission('manage_employees') || this.isOwner();
  }

  canManageServices() {
    return this.hasPermission('manage_services') || this.isOwner();
  }

  canViewAnalytics() {
    return this.hasPermission('view_analytics') || this.isOwner();
  }

  canManageBilling() {
    return this.isOwner(); // Only owners can manage billing
  }

  // Data Transformation Methods
  toFirebaseUser() {
    return {
      uid: this.uid,
      email: this.email,
      emailVerified: this.emailVerified,
      displayName: this.displayName,
      phoneNumber: this.phoneNumber,
      disabled: this.disabled
    };
  }

  toBusinessUserData() {
    return {
      businessId: this.businessId,
      role: this.role,
      permissions: this.permissions,
      createdAt: this.createdAt,
      lastLoginAt: this.lastLoginAt
    };
  }

  toPublicProfile() {
    return {
      uid: this.uid,
      displayName: this.displayName,
      role: this.role,
      emailVerified: this.emailVerified
    };
  }

  // Update Methods
  updateProfile(updates) {
    const allowedFields = ['displayName', 'phoneNumber'];
    
    allowedFields.forEach(field => {
      if (updates.hasOwnProperty(field)) {
        this[field] = updates[field];
      }
    });

    return this.validate();
  }

  updatePermissions(permissions) {
    if (!Array.isArray(permissions)) {
      return {
        isValid: false,
        errors: ['Permissions must be an array']
      };
    }

    const validPermissions = [
      'manage_employees',
      'manage_services', 
      'view_analytics',
      'manage_calendar',
      'handle_bookings'
    ];

    const invalidPermissions = permissions.filter(p => !validPermissions.includes(p));
    
    if (invalidPermissions.length > 0) {
      return {
        isValid: false,
        errors: [`Invalid permissions: ${invalidPermissions.join(', ')}`]
      };
    }

    this.permissions = permissions;
    return { isValid: true, errors: [] };
  }

  // Static Factory Methods
  static fromFirebaseUser(firebaseUser, businessData = {}) {
    return new User({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      emailVerified: firebaseUser.emailVerified,
      displayName: firebaseUser.displayName,
      phoneNumber: firebaseUser.phoneNumber,
      createdAt: firebaseUser.metadata?.creationTime,
      lastLoginAt: firebaseUser.metadata?.lastSignInTime,
      disabled: firebaseUser.disabled,
      ...businessData
    });
  }

  static fromRegistrationData(registrationData) {
    return new User({
      email: registrationData.ownerEmail,
      displayName: registrationData.ownerName,
      phoneNumber: registrationData.phoneNumber,
      role: 'owner',
      permissions: [], // Owners get all permissions by default
      businessId: registrationData.phoneNumber, // Using phone as business ID
      createdAt: new Date().toISOString()
    });
  }

  static createEmployee(employeeData, businessId) {
    return new User({
      email: employeeData.email,
      displayName: employeeData.name,
      phoneNumber: employeeData.phone,
      role: 'employee',
      permissions: employeeData.permissions || ['handle_bookings'],
      businessId: businessId,
      createdAt: new Date().toISOString()
    });
  }
}